import { IS_LOCAL, IS_TRAINING } from '../../config/env';
import { Injectable } from '../../decorators/injectable';
import type { GtagConsentParams, GtagConsentState } from '../../types/gtag';
import { isTokenizedPath, sanitizeTokenizedPath } from '../../utils/tokenized-path';
import type { UserConsentStatus } from '../consent/consent.service.interface';
import type { IAnalyticsService } from './analytics.service.interface';
import { readAnonymousConsent } from './anonymous-consent';
import { getIdentityEmailHash, setIdentityEmail as storeIdentityEmail } from './identity';

const ANALYTICS_CONSENT_TYPE = 'ANONYMIZED_STATISTICS';
const FUNCTIONALITY_CONSENT_TYPE = 'PLATFORM_IMPROVEMENT';
const MARKETING_CONSENT_TYPE = 'MARKETING';

/**
 * Conversion events carry the hashed email identity (Enhanced Conversions /
 * Advanced Matching) once it has been resolved. Kept as an allowlist so bulk
 * product events never carry identity without a measurement purpose.
 */
const IDENTITY_EVENT_ALLOWLIST = new Set(['sign_up', 'start_trial', 'checkout_success']);

/**
 * GTM distribution (mirror of the website's contract): every dispatch is two
 * pushes into `window.dataLayer` — a `{event_params: null}` reset first,
 * because GTM merges pushes into persistent state and the previous event's
 * params would otherwise leak into the next one. The container's Data Layer
 * Variables read `event_params.<key>`.
 *
 * Consent Mode v2, advanced implementation: events are always dispatched, and
 * the consent signals decide whether Google may use browser storage. Gating
 * the dispatch itself would degrade this back to a basic implementation.
 */
@Injectable()
export class AnalyticsService implements IAnalyticsService {
  private lastSignature: string | null = null;
  private lastUserConsents: UserConsentStatus[] | undefined;
  private lastTrackedPath: string | null = null;
  /**
   * consent_ready rimandato perché il visitatore è su una rotta con token
   * nell'URL: i pixel marketing (Meta/TikTok) leggono location.href e
   * lo spedirebbero intatto. Viene emesso al primo trackPageView su path pulito.
   */
  private deferredConsentReady: { analytics: string; marketing: string } | null = null;

  setIdentityEmail(email: string | undefined | null): void {
    storeIdentityEmail(email);
  }

  reapplyConsents(): void {
    this.applyConsents(this.lastUserConsents);
  }

  applyConsents(consents: UserConsentStatus[] | undefined): void {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }
    if (consents) {
      this.lastUserConsents = consents;
    }
    const source = consents ?? this.lastUserConsents;

    const anon = readAnonymousConsent();
    const marketing = this.effectiveConsent(source, MARKETING_CONSENT_TYPE, anon?.marketing);
    const analytics = this.effectiveConsent(source, ANALYTICS_CONSENT_TYPE, anon?.analytics);
    const functionality = this.isGranted(source, FUNCTIONALITY_CONSENT_TYPE);

    const params: GtagConsentParams = {
      analytics_storage: this.toState(analytics),
      functionality_storage: this.toState(functionality),
      personalization_storage: this.toState(functionality),
      security_storage: 'granted',
      ad_storage: this.toState(marketing),
      ad_user_data: this.toState(marketing),
      ad_personalization: 'denied',
    };

    const signature = JSON.stringify(params);
    if (signature === this.lastSignature) {
      return;
    }
    this.lastSignature = signature;

    // Training riusa la property GA di prod (id condiviso): non spedire eventi.
    if (IS_LOCAL || IS_TRAINING) {
      console.debug('[analytics] consent update', params);
      return;
    }

    // Il consenso (segnale legale verso gtag) è SEMPRE immediato; l'evento che
    // fa partire i tag marketing viene invece rimandato se la pagina corrente
    // ha un token nell'URL — i pixel leggono location.href e lo spedirebbero
    // intatto a Meta/TikTok. L'attesa dura finché il visitatore resta sulla
    // rotta tokenizzata: alla prima navigazione su path pulito parte.
    window.gtag('consent', 'update', params);
    const consentReady = {
      analytics: params.analytics_storage ?? 'denied',
      marketing: params.ad_storage ?? 'denied',
    };
    if (this.currentPathIsTokenized()) {
      this.deferredConsentReady = consentReady;
      return;
    }
    this.pushEvent('consent_ready', consentReady);
  }

  trackPageView(path: string): void {
    this.lastTrackedPath = path;
    this.flushDeferredConsentReady(path);
    this.dispatch(
      'page_view',
      {
        page_location: `${window.location.origin}${sanitizeTokenizedPath(path)}`,
        page_title: document.title,
      },
      () => typeof window !== 'undefined',
      '[analytics] page_view',
    );
  }

  /** La rotta corrente secondo l'ultima navigazione tracciata (fallback: location). */
  private currentPathIsTokenized(): boolean {
    const path = this.lastTrackedPath ?? window.location.pathname;
    return isTokenizedPath(path);
  }

  private flushDeferredConsentReady(path: string): void {
    if (!this.deferredConsentReady || isTokenizedPath(path)) {
      return;
    }
    const consentReady = this.deferredConsentReady;
    this.deferredConsentReady = null;
    this.pushEvent('consent_ready', consentReady);
  }

  trackEvent(name: string, params?: Record<string, unknown>): void {
    const enriched: Record<string, unknown> = { ...params };
    if (!enriched.em_sha256 && IDENTITY_EVENT_ALLOWLIST.has(name) && getIdentityEmailHash()) {
      enriched.em_sha256 = getIdentityEmailHash();
    }
    this.dispatch(name, enriched, () => typeof window !== 'undefined', `[analytics] event ${name}`);
  }

  private effectiveConsent(
    consents: UserConsentStatus[] | undefined,
    type: string,
    anonymousFallback: boolean | undefined,
  ): boolean {
    const row = consents?.find((c) => c.consentType === type);
    if (row) {
      return row.granted;
    }
    // No server-side record (visitor pre-signup, or consent not asked yet):
    // fall back to the anonymous banner so pixels don't wait for a login.
    return anonymousFallback ?? false;
  }

  private isGranted(consents: UserConsentStatus[] | undefined, type: string): boolean {
    return consents?.find((c) => c.consentType === type)?.granted ?? false;
  }

  private toState(granted: boolean): GtagConsentState {
    return granted ? 'granted' : 'denied';
  }

  /** Shared dispatch path: env gating + reset/event_params push convention. */
  private dispatch(
    name: string,
    params: Record<string, unknown>,
    environmentOk: () => boolean,
    debugLabel: string,
  ): void {
    if (!environmentOk()) {
      return;
    }
    if (IS_LOCAL || IS_TRAINING) {
      console.debug(debugLabel, params);
      return;
    }
    this.pushEvent(name, params);
  }

  private pushEvent(name: string, params?: Record<string, unknown>): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.dataLayer.push({ event_params: null });
    window.dataLayer.push({ event: name, event_params: params ?? {} });
  }
}
